import { useFormContext } from 'react-hook-form'
import { draftLineDiscount } from './einvoice-draft-line-row'
import type { EInvoiceDraftContent } from '@/lib/api/einvoice-api'
import type { EInvoiceDraftValues } from '@/lib/schemas/einvoice-draft-schemas'
import { m } from '@/lib/paraglide/messages'

const vnd = (amount: number) => amount.toLocaleString('vi-VN')

// Live invoice totals for the edited lines; also read by the editor's
// tracking to tell whether the saved invoice drifts from the order total.
export function draftTotals(
    content: EInvoiceDraftContent,
    items: EInvoiceDraftValues['items'],
) {
    let sale = content.total_sale_amount
    let discount = content.total_discount_amount
    let vat = content.total_vat_amount
    for (const edit of items) {
        const saved = content.items.find(
            (item) => item.line_number === edit.line_number,
        )!
        // Promotional lines never count towards the invoice totals.
        if (saved.item_type !== 1) continue
        if (edit.removed) {
            sale -= saved.amount
            discount -= saved.discount_amount
            vat -= saved.vat_amount
            continue
        }
        if (Number.isFinite(edit.discount_percent))
            discount +=
                draftLineDiscount(saved, edit.discount_percent) -
                saved.discount_amount
        if (Number.isFinite(edit.vat_amount))
            vat += edit.vat_amount - saved.vat_amount
    }
    const total =
        content.total_amount +
        (sale - content.total_sale_amount) -
        (discount - content.total_discount_amount) +
        (vat - content.total_vat_amount)
    return { sale, discount, vat, total }
}

export const draftTotalDiffers = (
    content: EInvoiceDraftContent,
    total: number,
) =>
    content.order_total != null &&
    Math.round(total) !== Math.round(content.order_total)

export function EInvoiceDraftTotals({
    content,
}: {
    content: EInvoiceDraftContent
}) {
    const { watch } = useFormContext<EInvoiceDraftValues>()
    const { sale, discount, vat, total } = draftTotals(content, watch('items'))
    const orderTotal = content.order_total
    return (
        <div className="space-y-2">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-muted p-4 text-sm tabular-nums">
                {[
                    [m.einvoice_draft_total_sale(), sale],
                    [m.einvoice_draft_total_discount(), discount],
                    [m.einvoice_draft_total_net(), sale - discount],
                    [m.einvoice_draft_total_vat(), vat],
                    [m.einvoice_draft_total(), total],
                ].map(([label, amount]) => (
                    <div
                        key={label}
                        className="col-span-2 grid grid-cols-subgrid last:font-semibold"
                    >
                        <dt>{label}</dt>
                        <dd aria-label={String(label)} className="text-right">
                            {vnd(Number(amount))} ₫
                        </dd>
                    </div>
                ))}
            </dl>
            {orderTotal != null && draftTotalDiffers(content, total) && (
                <p role="status" className="text-sm text-muted-foreground">
                    {m.einvoice_draft_order_total_differs({
                        invoice: vnd(total),
                        order: vnd(orderTotal),
                    })}
                </p>
            )}
        </div>
    )
}
